/**
 * Execution signals — main Krysalis API client (ISSUE-224).
 * Endpoints: `/api/v1/execution-signals/*` (see backend `execution_signals` router).
 * Used by default via `getExecutionSignalService()`; set `REACT_APP_EXO_EXECUTION_SIGNALS_MOCK=true` to use the mock instead.
 */

import axios from 'axios';
import { apiClient } from './apiClient';
import type {
  BlockSignalRequest,
  ClarificationRequest,
  EscalateSignalRequest,
  ExecutionSignal,
  ExoPanelStatus,
  IExecutionSignalService,
  NextSignalResponse,
  ResolveSignalRequest,
} from '../types/executionSignal';

const PREFIX = '/execution-signals';

function executionSignalErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const d = error.response?.data;
    if (d && typeof d === 'object' && d !== null && 'detail' in d) {
      const detail = (d as { detail: unknown }).detail;
      if (typeof detail === 'string') return detail;
    }
    if (error.response?.status === 401) return 'Authentication required';
    if (error.response?.status === 403) return 'Access denied';
    if (error.response?.status === 404) return 'Not found';
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

async function wrap<T>(fn: () => Promise<T>, fallback: string): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    throw new Error(executionSignalErrorMessage(e, fallback));
  }
}

export const executionSignalApiService: IExecutionSignalService = {
  async getNextSignal(): Promise<NextSignalResponse> {
    return wrap(async () => {
      const { data } = await apiClient.get<NextSignalResponse>(`${PREFIX}/next`);
      return data;
    }, 'Failed to load next signal');
  },

  async getPendingSignals(params?: {
    status?: string;
    priority_max?: number;
    limit?: number;
  }): Promise<ExecutionSignal[]> {
    return wrap(async () => {
      const { data } = await apiClient.get<ExecutionSignal[]>(`${PREFIX}/pending`, { params });
      return Array.isArray(data) ? data : [];
    }, 'Failed to load pending signals');
  },

  async getSignal(signalId: string): Promise<ExecutionSignal> {
    try {
      const { data } = await apiClient.get<ExecutionSignal>(`${PREFIX}/${signalId}`);
      return data;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        throw new Error(`Signal not found: ${signalId}`);
      }
      throw new Error(executionSignalErrorMessage(e, 'Failed to load signal'));
    }
  },

  async resolveSignal(signalId: string, resolution: ResolveSignalRequest): Promise<void> {
    return wrap(async () => {
      await apiClient.post(`${PREFIX}/${signalId}/resolve`, resolution);
    }, 'Failed to resolve signal');
  },

  async blockSignal(signalId: string, block: BlockSignalRequest): Promise<void> {
    return wrap(async () => {
      await apiClient.post(`${PREFIX}/${signalId}/block`, block);
    }, 'Failed to block signal');
  },

  async escalateSignal(signalId: string, escalation: EscalateSignalRequest): Promise<void> {
    return wrap(async () => {
      await apiClient.post(`${PREFIX}/${signalId}/escalate`, escalation);
    }, 'Failed to escalate signal');
  },

  async requestClarification(signalId: string, clarification: ClarificationRequest): Promise<void> {
    return wrap(async () => {
      await apiClient.post(`${PREFIX}/${signalId}/clarification`, clarification);
    }, 'Failed to request clarification');
  },

  async getStatus(): Promise<ExoPanelStatus> {
    return wrap(async () => {
      const { data } = await apiClient.get<ExoPanelStatus>(`${PREFIX}/status`);
      return data;
    }, 'Failed to load execution signal status');
  },
};
